import { useEffect, useState } from 'react';
import { Globe, Home, Building2, Instagram, ArrowUpRight, Share2 } from 'lucide-react';
import { BRAND_ASSETS } from '@/components/BrandLogo';
import { trackQrEvent, trackQrViewOnce } from '@/lib/qrTracking';
import './qr-landing.css';

const NAVY = '#0D1D2A';
const SHARE_URL = 'https://estadia365.com/qr';
const SHARE_TEXT = 'Estadía 365 · Alojamientos en Paraguay, Argentina y Brasil. Mirá los accesos rápidos:';
const WA_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z';

function WhatsAppIcon(props) {
  return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d={WA_PATH} /></svg>;
}

const LINKS = [
  { id: 'web', category: 'SITIO WEB', title: 'Quiénes somos', desc: 'Alojamientos, servicios y novedades.', href: 'https://estadia365.com/', Icon: Globe, primary: true },
  { id: 'travelers', category: 'PARA VIAJEROS', title: 'Encontrar alojamiento', desc: 'Hoteles, cabañas, departamentos y más.', href: 'https://estadia365.com/properties', Icon: Home },
  { id: 'owners', category: 'PARA PROPIETARIOS', title: 'Sumar mi alojamiento', desc: 'Más visibilidad, huéspedes y reservas.', href: 'https://estadia365.com/sumar-alojamiento', Icon: Building2 },
  { id: 'whatsapp', category: 'ATENCIÓN PERSONALIZADA', title: 'Hablar por WhatsApp', desc: 'Contacto directo con nuestro equipo.', href: 'https://wa.me/595982188147?text=Hola%20Estad%C3%ADa%20365%2C%20llegu%C3%A9%20desde%20el%20c%C3%B3digo%20QR%20y%20quisiera%20hacer%20una%20consulta.', Icon: WhatsAppIcon, external: true },
  { id: 'instagram', category: 'REDES SOCIALES', title: 'Ver Instagram', desc: 'Inspiración, destinos y novedades.', href: 'https://www.instagram.com/estadia365/', Icon: Instagram, external: true },
];

function setMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  const created = !el;
  if (created) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => { if (created) el.remove(); else el.setAttribute('content', previous ?? ''); };
}

function usePageMeta() {
  useEffect(() => {
    trackQrViewOnce();
    const prevTitle = document.title;
    document.title = 'Estadía 365 · ¿Cómo podemos ayudarte?';
    const restores = [
      setMeta('robots', 'noindex, nofollow'),
      setMeta('theme-color', NAVY),
      setMeta('description', 'Estadía 365 · Alquileres y ventas. Accesos rápidos: sitio web, alojamientos, propietarios, WhatsApp e Instagram.'),
    ];
    document.documentElement.classList.add('qr-html');
    return () => {
      document.title = prevTitle;
      restores.forEach((fn) => fn());
      document.documentElement.classList.remove('qr-html');
    };
  }, []);
}

function QrLink({ link }) {
  const { Icon } = link;
  const externalProps = link.external ? { target: '_blank', rel: 'noopener noreferrer' } : { rel: 'noopener noreferrer' };
  return (
    <li>
      <a href={link.href} {...externalProps} className={`qr-link${link.primary ? ' qr-link--primary' : ''}`}
        onClick={() => trackQrEvent('click', link.id)}
        aria-label={`${link.title}. ${link.desc}`} data-testid={`qr-link-${link.id}`}>
        <span className="qr-icon" aria-hidden="true"><Icon /></span>
        <span className="qr-text">
          <span className="qr-cat">{link.category}</span>
          <span className="qr-link-title">{link.title}</span>
          <span className="qr-desc">{link.desc}</span>
        </span>
        <ArrowUpRight className="qr-arrow" aria-hidden="true" />
      </a>
    </li>
  );
}

function ShareButton() {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    trackQrEvent('click', 'share');
    if (navigator.share) {
      setBusy(true);
      try { await navigator.share({ title: 'Estadía 365', text: SHARE_TEXT, url: SHARE_URL }); return; }
      catch (e) { if (e?.name === 'AbortError') return; }
      finally { setBusy(false); }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="qr-share-wrap">
      <button type="button" className="qr-share" onClick={share} disabled={busy} aria-label="Compartir esta página por WhatsApp" data-testid="qr-share-btn">
        <Share2 aria-hidden="true" /> Compartir
      </button>
    </div>
  );
}

export default function QrLandingPage() {
  usePageMeta();
  return (
    <div className="qr-page" data-testid="qr-landing-page">
      <div className="qr-bg" role="presentation" style={{ backgroundImage: 'url(/qr-assets/bridge.jpg)' }} />
      <div className="qr-overlay" role="presentation" />
      <main className="qr-main">
        <header className="qr-header">
          <img src={BRAND_ASSETS.logo.dark} alt="Estadía 365 - Alquileres y Ventas" className="qr-logo" width="1000" height="922" fetchPriority="high" data-testid="qr-logo" />
          <h1 className="qr-title" data-testid="qr-title">¿Cómo podemos ayudarte?</h1>
        </header>
        <nav aria-label="Accesos rápidos">
          <ul className="qr-links" data-testid="qr-links">
            {LINKS.map((link) => <QrLink key={link.id} link={link} />)}
          </ul>
        </nav>
        <ShareButton />
        <footer className="qr-footer">
          <p className="qr-years" data-testid="qr-years">+10 años</p>
          <p className="qr-exp">Experiencia y atención los 365 días</p>
          <hr className="qr-divider" />
          <p className="qr-countries" data-testid="qr-countries">PARAGUAY · ARGENTINA · BRASIL</p>
        </footer>
      </main>
    </div>
  );
}
